import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AdminSidebar from '../../components/ui/AdminSidebar';
import { supabase } from '../../utils/supabase';

const AdminGameReviews = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!user || userProfile?.role !== 'admin') {
      navigate('/');
      return;
    }

    loadPendingGames();
  }, [user, userProfile, navigate]);

  const loadPendingGames = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            owner_id
          ),
          game_category_mappings (
            game_categories (
              name
            )
          )
        `)
        .eq('status', 'under_review')
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('Failed to load pending games');
        console.error('Error loading games:', fetchError);
        return;
      }

      setGames(data || []);
    } catch (err) {
      setError('Failed to load pending games');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (gameId) => {
    if (!confirm('Are you sure you want to approve this game? It will be published immediately.')) {
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('games')
        .update({
          status: 'published',
          published_at: new Date().toISOString()
        })
        .eq('id', gameId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      // Reload games
      await loadPendingGames();
      setSelectedGame(null);

      alert('Game approved and published successfully!');
    } catch (err) {
      setError(`Failed to approve game: ${err.message}`);
      console.error('Error approving game:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedGame) return;

    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('games')
        .update({
          status: 'draft'
          // Note: You might want to add a rejection_reason field to the games table
        })
        .eq('id', selectedGame.id);

      if (updateError) {
        throw new Error(updateError.message);
      }

      // Reload games
      await loadPendingGames();
      setSelectedGame(null);
      setShowRejectModal(false);
      setRejectReason('');

      alert('Game rejected successfully.');
    } catch (err) {
      setError(`Failed to reject game: ${err.message}`);
      console.error('Error rejecting game:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (game) => {
    setSelectedGame(game);
    setShowRejectModal(true);
    setRejectReason('');
    setError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading pending games...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Game Reviews</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Review and approve games submitted by studios
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={loadPendingGames}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <main className="p-4 lg:p-8">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <span className="text-sm text-destructive">{error}</span>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Review</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{games.length}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-yellow-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Games List */}
          {games.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="CheckCircle" size={64} className="text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">All Clear!</h3>
              <p className="text-muted-foreground">
                There are no games pending review at the moment.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {games.map((game) => (
                <div key={game.id} className="bg-card border border-border rounded-lg p-6">
                  <div className="flex gap-6">
                    {/* Game Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {game.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            By {game.gaming_studios?.name || 'Unknown Studio'}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Under Review
                        </span>
                      </div>

                      <p className="text-foreground mb-4">{game.description}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Category</p>
                          <p className="text-sm font-medium text-foreground">
                            {game.game_category_mappings?.[0]?.game_categories?.name || 'Uncategorized'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Rating</p>
                          <p className="text-sm font-medium text-foreground">{game.rating}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Tier</p>
                          <p className="text-sm font-medium text-foreground capitalize">{game.tier}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Submitted</p>
                          <p className="text-sm font-medium text-foreground">
                            {new Date(game.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Game URL */}
                      {game.webgpu_url && (
                        <div className="bg-muted/30 rounded-lg p-3 mb-4">
                          <p className="text-xs text-muted-foreground mb-1">Game URL</p>
                          <a
                            href={game.webgpu_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline break-all"
                          >
                            {game.webgpu_url}
                          </a>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-3">
                        {game.webgpu_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="ExternalLink"
                            onClick={() => window.open(game.webgpu_url, '_blank')}
                          >
                            Test Game
                          </Button>
                        )}
                        <Button
                          variant="default"
                          size="sm"
                          iconName="Check"
                          onClick={() => handleApprove(game.id)}
                          disabled={actionLoading}
                        >
                          Approve & Publish
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="X"
                          onClick={() => openRejectModal(game)}
                          disabled={actionLoading}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Reject Game</h3>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedGame(null);
                  setRejectReason('');
                }}
              />
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to reject "{selectedGame?.title}"? This will move it back to draft status.
            </p>

            <div className="mb-4">
              <label className="text-sm font-medium text-foreground block mb-2">
                Reason for Rejection <span className="text-destructive">*</span>
              </label>
              <textarea
                placeholder="Explain why this game is being rejected..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                rows={4}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedGame(null);
                  setRejectReason('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                loading={actionLoading}
                disabled={actionLoading}
              >
                Reject Game
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGameReviews;
